import React, { useRef, useState } from 'react';
import apiClient from '../../../utils/api';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Slider } from '../../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { toast } from 'sonner';
import { Music, Mic, Upload, Loader2, Play } from 'lucide-react';

/* ── Waveform extraction via Web Audio API ───────────────── */
async function extractWaveform(source, numBars = 200) {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    let arrayBuffer;
    if (source instanceof File) {
      arrayBuffer = await source.arrayBuffer();
    } else if (typeof source === 'string') {
      // base64 data URL
      const res = await fetch(source);
      arrayBuffer = await res.arrayBuffer();
    } else return null;
    const decoded = await audioCtx.decodeAudioData(arrayBuffer);
    const raw = decoded.getChannelData(0);
    const step = Math.floor(raw.length / numBars);
    const bars = [];
    for (let i = 0; i < numBars; i++) {
      let sum = 0;
      for (let j = 0; j < step; j++) sum += Math.abs(raw[i * step + j]);
      bars.push(sum / step);
    }
    const peak = Math.max(...bars, 0.001);
    audioCtx.close();
    return bars.map(b => parseFloat((b / peak).toFixed(3)));
  } catch { return null; }
}

const VOICES = [
  { id: 'shimmer', name: 'Shimmer', desc: 'Bright, cheerful' },
  { id: 'alloy', name: 'Alloy', desc: 'Neutral, balanced' },
  { id: 'nova', name: 'Nova', desc: 'Energetic, upbeat' },
  { id: 'onyx', name: 'Onyx', desc: 'Deep, authoritative' },
  { id: 'echo', name: 'Echo', desc: 'Smooth, calm' },
  { id: 'fable', name: 'Fable', desc: 'Expressive' },
];

export const AudioMixer = ({ audio, onAudioChange, assets, onAssetsChange, onAddAudioSegment }) => {
  const audioInputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [generatingVO, setGeneratingVO] = useState(false);

  const segments = audio.segments || [];
  const musicSegs = segments.filter(s => s.type === 'music');
  const voSegs = segments.filter(s => s.type === 'voiceover');

  const handleMusicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      // Extract waveform from local file before uploading
      const waveform = await extractWaveform(file, Math.min(500, Math.max(80, Math.round(file.size / 10000))));

      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post('/reels/upload-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onAddAudioSegment) {
        onAddAudioSegment({
          id: `music_${Date.now()}`,
          type: 'music',
          asset_id: res.data.asset_id,
          name: file.name.replace(/\.[^/.]+$/, ''),
          start_time: 0,
          trim_start: 0,
          duration: res.data.duration || 60,
          volume: 70,
          waveform: waveform || [],
        });
      }
      onAudioChange({ music_asset_id: res.data.asset_id, music_filename: file.name });
      onAssetsChange();
      toast.success('Music added to timeline');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleGenerateVO = async () => {
    if (!audio.voiceover_text?.trim()) { toast.error('Enter voiceover text first'); return; }
    setGeneratingVO(true);
    try {
      const res = await apiClient.post('/reels/voiceover-preview', {
        text: audio.voiceover_text,
        voice: audio.voiceover_voice || 'shimmer',
      });
      if (res.data.success) {
        const dataUrl = `data:audio/mp3;base64,${res.data.audio_base64}`;

        // Get actual duration from backend, or measure from audio element
        let actualDuration = res.data.duration;
        if (!actualDuration) {
          actualDuration = await new Promise((resolve) => {
            const tempAudio = new Audio(dataUrl);
            tempAudio.addEventListener('loadedmetadata', () => resolve(Math.round(tempAudio.duration * 100) / 100));
            tempAudio.addEventListener('error', () => resolve(3));
            setTimeout(() => resolve(3), 3000);
          });
        }

        const waveform = await extractWaveform(dataUrl, Math.min(300, Math.max(50, Math.round(actualDuration * 15))));

        if (onAddAudioSegment) {
          onAddAudioSegment({
            id: `vo_${Date.now()}`,
            type: 'voiceover',
            name: `VO: ${(audio.voiceover_text || '').slice(0, 30)}...`,
            start_time: 0,
            trim_start: 0,
            duration: actualDuration,
            volume: 100,
            text: audio.voiceover_text,
            voice: audio.voiceover_voice || 'shimmer',
            waveform: waveform || [],
          });
        }
        new Audio(dataUrl).play();
        toast.success(`Voiceover added (${actualDuration.toFixed(1)}s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Voiceover generation failed');
    }
    setGeneratingVO(false);
  };

  return (
    <div className="border border-border/50 rounded-lg bg-card p-3 space-y-3" data-testid="audio-mixer">
      {/* Background Music */}
      <div>
        <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
          <Music className="h-4 w-4" /> Background Music
        </Label>
        {musicSegs.length > 0 ? (
          <div className="space-y-1">
            {musicSegs.map(seg => (
              <div key={seg.id} className="flex items-center gap-2 p-1.5 bg-emerald-500/10 rounded text-xs border border-emerald-500/20">
                <Music className="h-3 w-3 text-emerald-400" />
                <span className="flex-1 truncate">{seg.name}</span>
                <span className="text-[10px] text-muted-foreground">{seg.volume}%</span>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full text-xs mt-1" onClick={() => audioInputRef.current?.click()} disabled={uploading}>
              <Upload className="h-3 w-3 mr-1" /> Add Another Track
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => audioInputRef.current?.click()} disabled={uploading} data-testid="upload-music-btn">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
            Upload Music
          </Button>
        )}
        <input ref={audioInputRef} type="file" accept="audio/*" hidden onChange={handleMusicUpload} />
      </div>

      {/* AI Voiceover */}
      <div className="pt-3 border-t border-border/30">
        <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
          <Mic className="h-4 w-4" /> AI Voiceover
        </Label>
        <div className="space-y-2">
          <Select value={audio.voiceover_voice || 'shimmer'} onValueChange={(val) => onAudioChange({ voiceover_voice: val })}>
            <SelectTrigger className="h-7 text-xs" data-testid="voice-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VOICES.map(v => (
                <SelectItem key={v.id} value={v.id}><span>{v.name}</span> <span className="text-muted-foreground ml-1">— {v.desc}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea value={audio.voiceover_text || ''} onChange={(e) => onAudioChange({ voiceover_text: e.target.value })}
            placeholder="Enter voiceover script..." className="text-xs min-h-[60px] resize-none" maxLength={4096} data-testid="voiceover-text" />
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={handleGenerateVO}
            disabled={generatingVO || !audio.voiceover_text?.trim()} data-testid="generate-vo-btn">
            {generatingVO ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
            {generatingVO ? 'Generating...' : 'Generate & Add to Timeline'}
          </Button>
          {voSegs.length > 0 && (
            <div className="space-y-1">
              {voSegs.map(seg => (
                <div key={seg.id} className="flex items-center gap-2 p-1.5 bg-amber-500/10 rounded text-xs border border-amber-500/20">
                  <Mic className="h-3 w-3 text-amber-400" />
                  <span className="flex-1 truncate">{seg.name}</span>
                  <span className="text-[10px] text-muted-foreground">{seg.volume}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Auto-Duck Setting */}
      {musicSegs.length > 0 && voSegs.length > 0 && (
        <div className="pt-3 border-t border-border/30">
          <Label className="text-xs text-muted-foreground">Auto-Duck: Music lowers to {Math.round((audio.duck_music_to || 0.2) * 100)}% during voiceover</Label>
          <Slider value={[(audio.duck_music_to || 0.2) * 100]} onValueChange={([val]) => onAudioChange({ duck_music_to: val / 100 })} max={100} step={5} />
        </div>
      )}
    </div>
  );
};
